import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  CheckCircle,
  Clock,
  FileText,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { Button, Card, CardHeader, CardTitle, CardContent, ConfirmDialog } from '../components/ui';
import { RichTextEditor } from '../components/RichTextEditor';
import { RichTextDisplay } from '../components/RichTextDisplay';
import { TaskTemplate, Category } from '../types';
import { apiService } from '../services/api';
import { getIconComponent } from '../utils/icons';






export function Templates() {
  const [templateTasks, setTemplateTasks] = useState<TaskTemplate[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingTask, setEditingTask] = useState<TaskTemplate | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<TaskTemplate | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for timeout cleanup
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper functions for timeout management
  const setSuccessTimeout = (callback: () => void, delay: number = 5000) => {
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(callback, delay);
  };

  const setErrorTimeout = (callback: () => void, delay: number = 5000) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(callback, delay);
  };


  useEffect(() => {
    loadData();
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      // First, run migration to ensure all items have order fields
      try {
        await apiService.migrateOrderFields();
      } catch (migrationErr) {
        console.warn('Migration failed, continuing with existing data:', migrationErr);
      }
      
      const [templates, categoriesData] = await Promise.all([
        apiService.getTemplates(),
        apiService.getCategories()
      ]);
      setTemplateTasks(templates);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-deepAccent-500';
      case 'medium': return 'text-primary-500';
      case 'low': return 'text-neutral-500';
      default: return 'text-neutral-500';
    }
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'bg-neutral-100 text-neutral-600';
    
    switch (category.color) {
      case 'primary': return 'bg-primary-100 text-primary-700';
      case 'secondary': return 'bg-secondary-100 text-secondary-700';
      case 'accent': return 'bg-accent-100 text-accent-700';
      case 'deepAccent': return 'bg-deepAccent-100 text-deepAccent-700';
      case 'purple': return 'bg-purple-100 text-purple-700';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };

  const handleSaveTask = async (task: Partial<TaskTemplate>) => {
    try {
      if (editingTask) {
        const updatedTask = await apiService.updateTemplate(editingTask.id, task);
        setTemplateTasks(prev => prev.map(t => t.id === editingTask.id ? updatedTask : t));
      } else {
        const newTask = await apiService.createTemplate({
          ...task,
          isActive: true,
        } as TaskTemplate);
        setTemplateTasks(prev => [...prev, newTask]);
      }
      setEditingTask(null);
      setShowAddTask(false);
      setShowSuccessMessage(editingTask ? 'Template updated successfully!' : 'Template created successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleDeleteTask = (taskId: string) => {
    const task = templateTasks.find(t => t.id === taskId);
    if (!task) return;
    
    setDeletingTemplate(task);
  };

  const confirmDeleteTask = async () => {
    if (!deletingTemplate) return;
    
    try {
      await apiService.deleteTemplate(deletingTemplate.id);
      setTemplateTasks(prev => prev.filter(t => t.id !== deletingTemplate.id));
      setDeletingTemplate(null);
      setShowSuccessMessage('Template deleted successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error deleting template:', err);
      setDeletingTemplate(null);
      setError('Failed to delete template');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleToggleActive = async (task: TaskTemplate) => {
    try {
      const updatedTask = await apiService.updateTemplate(task.id, { isActive: !task.isActive });
      setTemplateTasks(prev => prev.map(t => t.id === task.id ? updatedTask : t));
      setShowSuccessMessage(`Template ${updatedTask.isActive ? 'activated' : 'deactivated'} successfully!`);
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error toggling template status:', err);
      setError('Failed to update template status');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleToggleCategoryActive = async (category: Category) => {
    try {
      const updatedCategory = await apiService.updateCategory(category.id, { isActive: !category.isActive });
      setCategories(prev => prev.map(c => c.id === category.id ? updatedCategory : c));
      setShowSuccessMessage(`Category ${updatedCategory.isActive ? 'activated' : 'deactivated'} successfully!`);
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error toggling category status:', err);
      setError('Failed to update category status');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleSaveCategory = async (category: Partial<Category>) => {
    try {
      if (editingCategory) {
        const updatedCategory = await apiService.updateCategory(editingCategory.id, category);
        setCategories(prev => prev.map(c => c.id === editingCategory.id ? updatedCategory : c));
      } else {
        const newCategory = await apiService.createCategory({
          ...category,
          isActive: true,
        } as Category);
        setCategories(prev => [...prev, newCategory]);
      }
      setEditingCategory(null);
      setShowAddCategory(false);
      setShowSuccessMessage(editingCategory ? 'Category updated successfully!' : 'Category created successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleDeleteCategory = (categoryId: string) => {
    // Don't allow deletion if tasks are using this category
    const tasksUsingCategory = templateTasks.filter(t => t.category === categoryId);
    if (tasksUsingCategory.length > 0) {
      setError('Cannot delete category that has tasks assigned to it');
      setErrorTimeout(() => setError(null));
      return;
    }
    
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    setDeletingCategory(category);
  };

  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;
    
    try {
      await apiService.deleteCategory(deletingCategory.id);
      setCategories(prev => prev.filter(c => c.id !== deletingCategory.id));
      setDeletingCategory(null);
      setShowSuccessMessage('Category deleted successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error deleting category:', err);
      setDeletingCategory(null);
      setError('Failed to delete category');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleMoveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    console.log(`🔄 Moving category ${categoryId} ${direction}`);
    
    const currentIndex = categories.findIndex(c => c.id === categoryId);
    if (currentIndex === -1) {
      console.error('Category not found');
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) {
      console.log('Cannot move further in this direction');
      return;
    }

    try {
      const currentCategory = categories[currentIndex];
      const targetCategory = categories[newIndex];
      
      // Ensure order fields exist, use index as fallback
      const currentOrder = currentCategory.order ?? currentIndex + 1;
      const targetOrder = targetCategory.order ?? newIndex + 1;
      
      console.log(`📋 Current category: ${currentCategory.name} (order: ${currentOrder})`);
      console.log(`📋 Target category: ${targetCategory.name} (order: ${targetOrder})`);
      
      // Swap orders
      await Promise.all([
        apiService.updateCategoryOrder(categoryId, targetOrder),
        apiService.updateCategoryOrder(targetCategory.id, currentOrder)
      ]);
      
      console.log('✅ Orders updated successfully');
      
      // Reload categories to get updated order
      const updatedCategories = await apiService.getCategories();
      setCategories(updatedCategories);
      
      console.log('✅ Categories reloaded');
    } catch (err) {
      console.error('Error moving category:', err);
      setError('Failed to move category');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleMoveTemplate = async (templateId: string, direction: 'up' | 'down') => {
    const currentTemplate = templateTasks.find(t => t.id === templateId);
    if (!currentTemplate) return;

    const templatesInCategory = templateTasks.filter(t => t.category === currentTemplate.category);
    const currentIndex = templatesInCategory.findIndex(t => t.id === templateId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= templatesInCategory.length) return;

    try {
      const targetTemplate = templatesInCategory[newIndex];
      
      // Ensure order fields exist, use index as fallback
      const currentOrder = currentTemplate.order ?? currentIndex + 1;
      const targetOrder = targetTemplate.order ?? newIndex + 1;
      
      console.log(`📋 Moving template: ${currentTemplate.title} (order: ${currentOrder}) to ${targetTemplate.title} (order: ${targetOrder})`);
      
      // Swap orders
      await Promise.all([
        apiService.updateTemplateOrder(templateId, targetOrder),
        apiService.updateTemplateOrder(targetTemplate.id, currentOrder)
      ]);
      
      // Reload templates to get updated order
      const updatedTemplates = await apiService.getTemplates();
      setTemplateTasks(updatedTemplates);
    } catch (err) {
      console.error('Error moving template:', err);
      setError('Failed to move template');
      setErrorTimeout(() => setError(null));
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center justify-between">
          <div className="space-y-3">
            <h1 className="text-5xl font-display font-bold text-neutral-900 tracking-tight">Task Templates</h1>
            <p className="text-neutral-700 text-lg font-text font-medium leading-relaxed max-w-2xl">
              Manage template tasks and categories that will be automatically added to new hotels
            </p>
          </div>
          
          <div className="flex items-center space-x-4">

            <Button 
              variant="outline" 
              size="lg"
              onClick={() => setShowAddCategory(true)}
              className="bg-white/40 backdrop-blur-xl border-neutral-200"
            >
              <Plus className="w-5 h-5 mr-3" />
              <span className="font-bold">Add Category</span>
            </Button>
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => setShowAddTask(true)}
              className="shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
            >
              <Plus className="w-5 h-5 mr-3" />
              <span className="font-bold">Add Template Task</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl">
          <p className="text-green-700">{showSuccessMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Categories Section */}
        <div className="lg:col-span-1">
          <Card className="bg-white/30 backdrop-blur-xl border-neutral-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-display font-bold text-neutral-900">Categories</CardTitle>
              <p className="text-neutral-600 font-text">Manage task categories</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categories.map((category) => {
                  const IconComponent = getIconComponent(category.icon);
                  return (
                    <div 
                      key={category.id}
                      className={`flex items-center justify-between p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-neutral-200 hover:bg-white/60 transition-all duration-200 ${
                        !category.isActive ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getCategoryColor(category.id)}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-neutral-900">{category.name}</h3>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Move buttons */}
                        <div className="flex flex-col space-y-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveCategory(category.id, 'up')}
                            disabled={categories.indexOf(category) === 0}
                            className="text-neutral-600 hover:text-neutral-900 p-1 h-6 w-6"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveCategory(category.id, 'down')}
                            disabled={categories.indexOf(category) === categories.length - 1}
                            className="text-neutral-600 hover:text-neutral-900 p-1 h-6 w-6"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleCategoryActive(category)}
                          className={`text-neutral-600 hover:text-neutral-900 ${
                            category.isActive ? 'hover:text-green-600' : 'hover:text-gray-600'
                          }`}
                        >
                          {category.isActive ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingCategory(category)}
                          className="text-neutral-600 hover:text-neutral-900"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-neutral-600 hover:text-deepAccent-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Template Tasks Section */}
        <div className="lg:col-span-2">
          <Card className="bg-white/30 backdrop-blur-xl border-neutral-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-display font-bold text-neutral-900">Template Tasks</CardTitle>
              <p className="text-neutral-600 font-text">Tasks that will be added to new hotels</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Group templates by category */}
                {categories
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((category, index) => {
                    const categoryTemplates = templateTasks.filter(task => task.category === category.id);
                    if (categoryTemplates.length === 0) return null;
                    
                    const activeTemplates = categoryTemplates.filter(task => task.isActive).length;
                    const progressPercentage = Math.round((activeTemplates / categoryTemplates.length) * 100);
                    
                    return (
                      <div key={category.id} className="space-y-4">
                        <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-neutral-200 overflow-hidden">
                          <div className="p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                  index % 4 === 0 ? 'bg-blue-100 text-blue-600' :
                                  index % 4 === 1 ? 'bg-emerald-100 text-emerald-600' :
                                  index % 4 === 2 ? 'bg-purple-100 text-purple-600' :
                                  'bg-orange-100 text-orange-600'
                                }`}>
                                  <span className="text-xl font-bold">{index + 1}</span>
                                </div>
                                <div>
                                  <h4 className="text-2xl font-display font-bold text-neutral-900 capitalize">
                                    {category.name}
                                  </h4>
                                  <p className="text-neutral-600 font-text">
                                    {activeTemplates} of {categoryTemplates.length} templates active
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <div className="text-3xl font-display font-bold text-neutral-900 mb-1">
                                    {progressPercentage}%
                                  </div>
                                  <div className="w-20 bg-neutral-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-500 ${
                                        progressPercentage === 100 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                                        progressPercentage > 50 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                                        'bg-gradient-to-r from-orange-400 to-orange-500'
                                      }`}
                                      style={{ width: `${progressPercentage}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {categoryTemplates
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((task) => (
                            <div 
                              key={task.id}
                              className={`p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-neutral-200 hover:bg-white/60 transition-all duration-200 ${
                                !task.isActive ? 'opacity-60' : ''
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h3 className="text-lg font-display font-bold text-neutral-900">{task.title}</h3>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)} bg-neutral-100`}>
                                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                      task.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {task.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                  <div className="text-neutral-600 font-text text-sm leading-relaxed">
                                    <RichTextDisplay content={task.description} />
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                  {/* Move buttons */}
                                  <div className="flex flex-col space-y-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleMoveTemplate(task.id, 'up')}
                                      disabled={categoryTemplates.indexOf(task) === 0}
                                      className="text-neutral-600 hover:text-neutral-900 p-1 h-6 w-6"
                                    >
                                      <ChevronUp className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleMoveTemplate(task.id, 'down')}
                                      disabled={categoryTemplates.indexOf(task) === categoryTemplates.length - 1}
                                      className="text-neutral-600 hover:text-neutral-900 p-1 h-6 w-6"
                                    >
                                      <ChevronDown className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleActive(task)}
                                    className={`text-neutral-600 hover:text-neutral-900 ${
                                      task.isActive ? 'hover:text-green-600' : 'hover:text-gray-600'
                                    }`}
                                  >
                                    {task.isActive ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingTask(task)}
                                    className="text-neutral-600 hover:text-neutral-900"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTask(task.id)}
                                    className="text-neutral-600 hover:text-deepAccent-500"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                
                {/* Show message if no templates */}
                {templateTasks.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-neutral-400" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-neutral-700 mb-2">No Template Tasks</h3>
                    <p className="text-neutral-500 font-text">Create your first template task to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Task Modal */}
      {(showAddTask || editingTask) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                {editingTask ? 'Edit Template Task' : 'Add Template Task'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddTask(false);
                  setEditingTask(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <TaskForm
              task={editingTask}
              categories={categories}
              onSave={handleSaveTask}
              onCancel={() => {
                setShowAddTask(false);
                setEditingTask(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Add/Edit Category Modal */}
      {(showAddCategory || editingCategory) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddCategory(false);
                  setEditingCategory(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <CategoryForm
              category={editingCategory}
              onSave={handleSaveCategory}
              onCancel={() => {
                setShowAddCategory(false);
                setEditingCategory(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Confirm Delete Template Dialog */}
      <ConfirmDialog
        isOpen={deletingTemplate !== null}
        onClose={() => setDeletingTemplate(null)}
        onConfirm={confirmDeleteTask}
        title="Delete Template"
        message={deletingTemplate ? `Are you sure you want to delete template "${deletingTemplate.title}"?` : ''}
        variant="danger"
        confirmText="Delete Template"
      />

      {/* Confirm Delete Category Dialog */}
      <ConfirmDialog
        isOpen={deletingCategory !== null}
        onClose={() => setDeletingCategory(null)}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message={deletingCategory ? `Are you sure you want to delete category "${deletingCategory.name}"?` : ''}
        variant="danger"
        confirmText="Delete Category"
      />
    </div>
  );
}

// Task Form Component
interface TaskFormProps {
  task?: TaskTemplate | null;
  categories: any[];
  onSave: (task: Partial<TaskTemplate>) => void;
  onCancel: () => void;
}

function TaskForm({ task, categories, onSave, onCancel }: TaskFormProps) {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    category: task?.category || 'setup',
    priority: task?.priority || 'medium' as 'low' | 'medium' | 'high',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Task Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter task title"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Description
        </label>
        <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
          placeholder="Enter task description"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as string }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          <Save className="w-4 h-4 mr-2" />
          Save Task
        </Button>
      </div>
    </form>
  );
}

// Category Form Component
interface CategoryFormProps {
  category?: Category | null;
  onSave: (category: Partial<Category>) => void;
  onCancel: () => void;
}

function CategoryForm({ category, onSave, onCancel }: CategoryFormProps) {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    id: category?.id || '',
    color: category?.color || 'primary' as Category['color'],
    icon: category?.icon || 'Settings',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Category Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter category name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Category ID
        </label>
        <input
          type="text"
          value={formData.id}
          onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="category-id"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Icon
        </label>
        <select
          value={formData.icon}
          onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
        >
          <option value="Settings">Settings</option>
          <option value="FileText">FileText</option>
          <option value="CheckCircle">CheckCircle</option>
          <option value="Building2">Building2</option>
          <option value="AlertCircle">AlertCircle</option>
          <option value="Clock">Clock</option>
          <option value="Users">Users</option>
          <option value="Mail">Mail</option>
          <option value="Shield">Shield</option>
          <option value="Plus">Plus</option>
          <option value="Edit3">Edit3</option>
          <option value="Trash2">Trash2</option>
          <option value="Save">Save</option>
          <option value="X">X</option>
          <option value="ArrowLeft">ArrowLeft</option>
          <option value="Calendar">Calendar</option>
          <option value="Filter">Filter</option>
          <option value="ChevronDown">ChevronDown</option>
          <option value="MessageSquare">MessageSquare</option>
          <option value="Database">Database</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Color Theme
        </label>
        <select
          value={formData.color}
          onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value as Category['color'] }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
        >
          <option value="primary">Coral</option>
          <option value="secondary">Light Pink</option>
          <option value="accent">Bright Pink</option>
          <option value="deepAccent">Burgundy</option>
          <option value="purple">Purple</option>
          <option value="neutral">Neutral</option>
        </select>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          <Save className="w-4 h-4 mr-2" />
          Save Category
        </Button>
      </div>
    </form>
  );
}
